import { Fragment } from "react";
import { useTranslations } from "next-intl";
import { IconFileText, IconShieldLock } from "@tabler/icons-react";
import { legalDocument, type LegalDocumentKey } from "@/lib/legal/documents";

const LINKS: ReadonlyArray<{ key: LegalDocumentKey; icon: React.ElementType }> = [
  { key: "imprint", icon: IconFileText },
  { key: "privacy", icon: IconShieldLock },
];

/**
 * Imprint + privacy policy, as plain external links.
 *
 * Two hosts, on purpose (decided 2026-10-12): every auth page — a visitor
 * without an account has no other way to reach either document — and the user
 * sidebar under Settings, the one place inside the app. The event and template
 * sidebars and the account menu used to carry them as well; Settings is one
 * click away from each of those surfaces, so the copies only repeated the same
 * two links. The targets come from the legal document table, the single place
 * a URL or version changes.
 *
 * Labels: `legal.imprint`, but NOT `legal.privacy` — that key was written for
 * the consent sentence ("I accept the …") and is a declined fragment in a
 * dozen locales (pl "Politykę prywatności", fi "tietosuojakäytännön", …).
 * `portal.privacyLink` is the standalone, nominative word in all 57 bundles.
 *
 * `iconOnlyBelowMd`: the user sidebar collapses to an icon bar on small screens,
 * where its text labels are hidden — the links keep an accessible name there.
 *
 * `inline`: the sidebar's quiet footer line, "Imprint · Privacy" in the
 * smallest body size. Still directly visible — an imprint has to be reachable
 * without opening a menu. Composes with the icon mode: where the icons show,
 * the `·` goes with the text it separates. The `·` is glued to the link after
 * it, so a wrapped line never ends on a dangling dot.
 */
export function LegalLinks({
  className = "",
  iconOnlyBelowMd = false,
  inline = false,
}: {
  className?: string;
  iconOnlyBelowMd?: boolean;
  inline?: boolean;
}) {
  const tLegal = useTranslations("legal");
  const tPortal = useTranslations("portal");
  const label = (key: LegalDocumentKey) =>
    key === "privacy" ? tPortal("privacyLink") : tLegal("imprint");
  // A document without a published URL has nothing to link to.
  const links = LINKS.flatMap((link) => {
    const href = legalDocument(link.key).href;
    return href ? [{ ...link, href }] : [];
  });
  return (
    // A group, not a <nav>: one of its two hosts is already inside one.
    <div role="group" aria-label={label("imprint") + " / " + label("privacy")} className={className}>
      {links.map(({ key, icon: Icon, href }, i) => {
        const link = (
          <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`legal-link-${key}`}
            aria-label={label(key)}
            // font-tertiary, not quaternary: #888 on white is 3.5:1 at 12px.
            // Icon mode gets the sidebar's own 40px target, not a bare 18px glyph;
            // the 10px inline text still keeps a 24px-high target (WCAG 2.5.8).
            className={`${inline ? "body-4-regular min-h-6" : "body-3-regular"} font-tertiary hover:font-secondary hover:underline inline-flex items-center gap-1.5 focus-ring rounded ${
              iconOnlyBelowMd ? "p-2.5 md:p-0" : ""
            }`}
          >
            {iconOnlyBelowMd && <Icon size={18} aria-hidden className="md:hidden" />}
            <span className={iconOnlyBelowMd ? "hidden md:inline" : undefined}>
              {label(key)}
            </span>
          </a>
        );
        if (!(inline && i > 0)) return <Fragment key={key}>{link}</Fragment>;
        // The `·` and the link after it are ONE unbreakable item. As separate
        // flex items a narrow sidebar with long words (cs-CZ, bg-BG, is-IS,
        // gd-GB) could break the line right after the dot and leave it
        // dangling at the end of line one; this way it starts line two.
        return (
          <span key={key} className="inline-flex items-center gap-x-1.5 whitespace-nowrap">
            <span
              aria-hidden="true"
              className={`body-4-regular font-tertiary${iconOnlyBelowMd ? " hidden md:inline" : ""}`}
            >
              ·
            </span>
            {link}
          </span>
        );
      })}
    </div>
  );
}
